import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { EupRoutesService } from '../core/eupRoutes.service';
import { Observable } from 'rxjs';
import { EupHttpHandler } from '../core/eupHttpHandler.service';
import { Language } from './generalInterfaces';
import { ILabCompanySettingsDto } from '../interfaces/ILabCompanySettingsDto';
import { ISettingsData } from '../interfaces/ISettingsData';

@Injectable()
export class SettingsService {
	private headers = new HttpHeaders({
		'Content-Type': 'application/json'
	});

	constructor(private http: EupHttpHandler, private eupRoutesService: EupRoutesService) {}

	getAllSecurityQuestions(): Observable<string[]> {
		return this.http.get(this.eupRoutesService.userSettings.getAllSecurityQuestions);
	}

	getVisibleUserAuthenticationDetails(userName: string): Observable<UserAuthenticationDetails> {
		return this.http.get(this.eupRoutesService.userSettings.getVisibleUserAuthenticationDetails + userName);
	}

	getSettings(companyId: number, navigateOnFailure = true, triggerSpinner = true): Observable<ISettingsData> {
		const params = new HttpParams().set('companyId', companyId.toString());
		return this.http.get(
			this.eupRoutesService.userSettings.getUrl,
			{ params: params },
			navigateOnFailure,
			triggerSpinner
		);
	}

	getExportGeneralSettings(companyId: number): Observable<GeneralExportSettingsData> {
		const params = new HttpParams().set('companyId', companyId.toString());
		return this.http.get(this.eupRoutesService.userSettings.getExportGeneralSettingsUrl, { params: params });
	}

	setSettings(key: string, value: string): Observable<any> {
		return this.http.post(this.eupRoutesService.userSettings.setUrl, { key, value }, { responseType: 'text' });
	}

	getLocalizationSettings(): Observable<any> {
		return this.http.get(this.eupRoutesService.userSettings.localizationSettingsUrl).map((res: any) => {
			return {
				languages: res.languages.map((o: any) => {
					return <Language>{ code: o.name, description: o.localizedName, englishName: o.englishName };
				}),
				dateFormats: res.dateFormats
			};
		});
	}

	setCredentials(credentials: SecurityInfoParams): Observable<boolean> {
		return this.http
			.post(this.eupRoutesService.userSettings.setCredentials, credentials, { headers: this.headers }, false)
			.map((res: any) => res.isSuccessful);
	}

	setLabCompanySettings(companySettings: ILabCompanySettingsDto): Observable<boolean> {
		return this.http.post(
			this.eupRoutesService.userSettings.setCompanySettingsUrl,
			companySettings,
			{ headers: this.headers },
			false
		);
	}

	getLabCompanySettings(navigateOnFailure = true, triggerSpinner = true): Observable<ILabCompanySettingsDto> {
		return this.http.get(
			this.eupRoutesService.userSettings.getCompanySettingsUrl,
			null,
			navigateOnFailure,
			triggerSpinner
		);
	}
}

export class SecurityInfoParams {
	username: string;
	currentPassword: string;
	confirmPassword: string;
	newPassword: string;
	securityQuestion: string;
	securityAnswer: string;
	loginEmail: string;
}

export class GeneralExportSettingsData {
	allowRestorativeExport: boolean;
	allowOrthodonticsExport: boolean;
}

export class UserSettings {
	caseTypeId: number;
	shadeSystemId: number;
	toothId: number;
	scanningPositionId: number;
	restorativeGuidanceOrderId: number;
	prepGuidanceOrderId: number;
	orthodonticGuidanceOrderId: number;
	scanArchFirstId: number;
	gyroOrientationId: number;
	touchPadOrientationId: number;
	scanningRangeEnableSuggestions: boolean;
	licenseNumber: string;
	signature: string;
	signatureOptionId: number;
	selectedLanguage: Language;
	dateFormat: number;
	exportTypeForiCast: number;
	exportTypeForiRecord: number;
	exportCadCamSystemType: number;
	dataFormat: number;
	fileType: number;
	niriEnabledDefault: boolean;
	patientAnonymization: boolean;
}

export class UserAuthenticationDetails {
	userId: number;
	email: string;
	passwordQuestion: string;
}
